import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { cardEnter } from './motion'

interface CardProps {
  children: ReactNode
  className?: string
  /** Opts into the staggered entrance. Off for content that must not move on refresh. */
  animate?: boolean
  as?: 'div' | 'section' | 'article'
  /** Corner rivets. Resolves to nothing in both themes now — see `Rivets`. */
  rivets?: boolean
  /** Lifts further on hover. For a housing that is itself a link or a button. */
  interactive?: boolean
}

/**
 * A housing: a surface step and a hairline edge.
 *
 * ── Why there is no shadow ──
 * Because it does not need one, in either theme, for opposite reasons. On the dark ground a
 * heavy shadow over near-black reads as a smudge rather than as height. On Paper the card is
 * a grey #eff2f7 fill on a white page, so the surface step IS the elevation and a shadow only
 * adds weight to a page whose whole character is the absence of it — the reference draws
 * `box-shadow: none` on every card.
 *
 * Note that this is a property of the CARD, not of the theme: `--lift` is a real shadow again
 * under Paper because drawers, modals and hover affordances genuinely need one. `Housing`
 * stays flat by not asking for it. `interactive` is a hairline change rather than a shadow
 * change for the same reason — see the `--lift` note in theme.css.
 *
 * ── The entrance, which must stay as it is ──
 * The entrance animation is SELF-DRIVEN (`initial`/`animate` set here), never inherited from
 * a parent's stagger orchestration. A card whose data or lazy chunk resolves after the
 * parent finished animating would otherwise mount into the parent's completed "animate"
 * state and stay pinned at opacity 0 — invisible, with its content present in the DOM and no
 * error anywhere. Self-driving costs the cross-card stagger and buys a card that is always
 * visible.
 */
export function Housing({
  children,
  className,
  animate = true,
  as = 'div',
  rivets = false,
  interactive = false,
}: CardProps) {
  const Component = motion[as]

  return (
    <Component
      variants={cardEnter}
      initial={animate ? 'initial' : false}
      animate="animate"
      className={cn(
        'surface-glass relative rounded-[20px] ring-1 ring-line',
        /*
         * A hairline of light along the TOP edge — the bevel a glass panel catches.
         *
         * White at 6%, which is a DARK-THEME value: it is doing real work over a near-black
         * card and is arithmetically invisible over Paper's #eff2f7, where the same 6% white
         * moves the top row of pixels by less than one step of 255. Kept rather than made
         * theme-conditional: it is already a no-op on the ground where it should not appear,
         * which is the cheapest possible way to be right in both themes, and raising it until
         * it showed on Paper would draw a second hairline just inside the ring — the exact
         * artefact `--edge-hi: transparent` exists to remove.
         *
         * Top only. A full inset ring bevels all four sides, which reads as a button.
         */
        'shadow-[inset_0_1px_0_rgb(255_255_255/0.06)]',
        interactive && 'transition-colors duration-200 hover:ring-line-strong',
        className,
      )}
    >
      {rivets && <Rivets />}
      {children}
    </Component>
  )
}

/**
 * Kept as an alias so pages not yet converted keep rendering. New code should say
 * `Housing` — the name is the one thing carrying the metaphor into the call site.
 */
export const Card = Housing

/**
 * Four corner rivets — which now draw NOTHING, in either theme.
 *
 * `--rivet` and `--rivet-shadow` are `none` in both blocks. Dark had already zeroed them, so
 * this component grew domed metal studs on a light card and nothing on a dark one: the same
 * housing, with hardware in one theme only. A rivet says "this is a machine housing", and the
 * card it decorated is a flat grey panel on a white page.
 *
 * The component and its `rivets` prop are kept rather than ripped out. They resolve to two
 * empty custom properties and cost nothing, and deleting a prop threaded through call sites in
 * order to express "draw nothing" is churn that makes the next theme harder to write, not
 * easier. Give `--rivet` a value and the hardware comes back everywhere at once.
 *
 * Purely decorative, so hidden from assistive technology.
 */
function Rivets() {
  return (
    <span aria-hidden>
      {(
        [
          'left-2.5 top-2.5',
          'right-2.5 top-2.5',
          'left-2.5 bottom-2.5',
          'right-2.5 bottom-2.5',
        ] as const
      ).map((pos) => (
        <span
          key={pos}
          // 8px, not 6: at 6 they read as dust specks on the glass rather than as hardware.
          className={cn('absolute size-2 rounded-full', pos)}
          style={{ background: 'var(--rivet)', boxShadow: 'var(--rivet-shadow)' }}
        />
      ))}
    </span>
  )
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode
  subtitle?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    /*
     * Title left, control right, on one line — the reference's card header exactly. Its
     * "Last 5 Days" refresh sits at the end of this row rather than above or below the
     * title, which is what keeps a section title and its range picker reading as one
     * control surface instead of two stacked rows.
     */
    <div className={cn('flex items-start justify-between gap-4 p-3 pb-0', className)}>
      <div className="min-w-0">
        {/* The shared section heading — see `.title-section` in theme.css. */}
        <h2 className="title-section">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  // 12px, from the reference. It was 20px, which on a card holding a chart and a tile
  // column spends most of a tile's width on inset.
  return <div className={cn('p-3', className)}>{children}</div>
}

export function CardFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('border-t border-line px-3 py-3 text-xs text-faint', className)}>
      {children}
    </div>
  )
}
